import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  PhaseId as ApiPhaseId,
  ContactDto,
  DecisionDto,
  DocumentDto,
  PhaseDto,
  PhotoDto,
} from '@/api/generated';
import { useContacts, useDecisions, useDocuments, usePhase, usePhotos } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import type { IconName } from '@/design-system/icon/Icon';
import { Icon } from '@/design-system/icon/Icon';
import { Avatar } from '@/design-system/primitives/Avatar/Avatar';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { PHASES, type PhaseId } from '@/domain/phases';
import {
  formatDayMonth,
  formatRelativeDeadline,
  formatRelativePast,
  hueForName,
  toNumber,
} from '@/lib/format';
import styles from './PhaseDetail.module.css';
import { MailPhaseContactsDialog, PhaseEditDialog } from './PhaseDialogs';

// Phase order from the canonical PHASES list, used for the "fase N / 10" eyebrow.
const PHASE_INDEX: Record<string, number> = Object.fromEntries(
  PHASES.map((p, i) => [p.id.toLowerCase(), i + 1]),
);

// 2026-05-17 — the per-phase static "summary" + sub-step grid was Phase-1
// design-handoff fixture data hard-coded for Ruwbouw only. They rendered
// unconditionally on /fases/ruw regardless of the real project state, so
// fresh users saw "Heyvaert nv staat in voor de ruwbouw, onder toezicht
// van Studio Vandeput" plus a 6-step done/doing/idle grid that had nothing
// to do with their build. Both removed; the SPA only renders fields the
// API actually returns. Sub-steps aren't modelled server-side (no entity);
// reintroduce here once they are.

const PHOTO_GRID_SIZE = 6;

export function PhaseDetail() {
  const { phaseId } = useParams<{ phaseId: string }>();
  const localId = (phaseId ?? 'ruw').toLowerCase() as PhaseId;
  const apiId = pascalCase(localId) as ApiPhaseId;

  const phaseQuery = usePhase(apiId);
  const contacts = useContacts({ pageSize: 50 });
  const docs = useDocuments({ phaseId: apiId, pageSize: 10 });
  const decisions = useDecisions({ pageSize: 20 });
  const photos = usePhotos({ phaseId: apiId, pageSize: 12 });

  return (
    <AppFrame>
      <QueryBoundary query={phaseQuery} loading="Fase laden…">
        {(phase) => (
          <PhaseDetailBody
            phase={phase}
            localId={localId}
            apiId={apiId}
            contacts={contacts.data?.items ?? []}
            documents={docs.data?.items ?? []}
            decisions={(decisions.data?.items ?? []).filter((d) => d.phaseId === apiId)}
            photos={photos.data?.items ?? []}
            photoTotal={toNumber(photos.data?.total ?? 0)}
          />
        )}
      </QueryBoundary>
    </AppFrame>
  );
}

function PhaseDetailBody({
  phase,
  localId,
  apiId,
  contacts,
  documents,
  decisions,
  photos,
  photoTotal,
}: {
  phase: PhaseDto;
  localId: PhaseId;
  apiId: ApiPhaseId;
  contacts: ContactDto[];
  documents: DocumentDto[];
  decisions: DecisionDto[];
  photos: PhotoDto[];
  photoTotal: number;
}) {
  const index = PHASE_INDEX[localId] ?? 5;
  const responsibles = responsiblesForPhase(apiId, contacts);
  const [editOpen, setEditOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);

  return (
    <>
      <div className={`row gap-6 ${styles.crumb}`}>
        <Link to="/tijdlijn" className={styles.crumbLink}>
          Tijdlijn
        </Link>
        <Icon name="chevR" size={11} />
        <span>{phase.label}</span>
      </div>

      <div className={`row ${styles.headerRow}`}>
        <div>
          <div className="row gap-10">
            <span className="eyebrow">Fase {String(index).padStart(2, '0')} / 10</span>
            {phase.status === 'Doing' &&
              phase.progress !== null &&
              phase.progress !== undefined && (
                <Pill kind="warn">Bezig · {Math.round(toNumber(phase.progress) * 100)}%</Pill>
              )}
            {phase.status === 'Done' && <Pill kind="ok">Afgerond</Pill>}
            {phase.status === 'Idle' && <Pill kind="idle">Te starten</Pill>}
            {phase.status === 'Late' && <Pill kind="bad">Vertraagd</Pill>}
            <span className={`mono ${styles.range}`}>
              {formatDayMonth(phase.start)} — {formatDayMonth(phase.end)}
            </span>
          </div>
          <h1 className={styles.title}>{phase.label}</h1>
        </div>
        <div className={`row gap-8 ml-auto ${styles.headerActions}`}>
          <Btn onClick={() => setMailOpen(true)}>
            <Icon name="mail" size={14} /> Mail verantwoordelijken
          </Btn>
          <Btn variant="primary" onClick={() => setEditOpen(true)}>
            <Icon name="edit" size={14} /> Bewerk fase
          </Btn>
        </div>
      </div>
      <PhaseEditDialog open={editOpen} onOpenChange={setEditOpen} phase={phase} />
      <MailPhaseContactsDialog
        open={mailOpen}
        onOpenChange={setMailOpen}
        phase={phase}
        responsibles={responsibles}
      />

      {/* Sub-step grid + Ruwbouw summary removed 2026-05-17 — both were
          Phase-1 fixture data that rendered unconditionally on /fases/ruw
          regardless of the project's actual state. */}

      <div className={styles.body}>
        <div className={`col gap-16 ${styles.bodyLeft}`}>
          <Card className={styles.sectionCard}>
            <h3 className={styles.sectionTitle}>Verantwoordelijke partijen</h3>
            {responsibles.length === 0 ? (
              <div className="mono c-ink-3">Geen toegewezen partijen.</div>
            ) : (
              <div className={`col gap-12 ${styles.responsibleList}`}>
                {responsibles.map((r) => (
                  <div key={r.id} className="row gap-12">
                    <Avatar name={r.name} size={36} hue={hueForName(r.name)} />
                    <div className="flex-1">
                      <div className={`row gap-6 ${styles.responsibleName}`}>
                        <span>{r.name}</span>
                        {r.primary && <Pill kind="accent">primair</Pill>}
                      </div>
                      <div className={styles.responsibleMeta}>
                        {r.company ?? r.role} · {r.role}
                      </div>
                    </div>
                    {r.phone ? (
                      <a
                        href={`tel:${r.phone.replace(/\s+/g, '')}`}
                        className={styles.telLink}
                        aria-label={`Bel ${r.name}`}
                      >
                        <Icon name="phone" size={14} />
                      </a>
                    ) : (
                      <Btn variant="ghost" disabled aria-label={`Bel ${r.name}`}>
                        <Icon name="phone" size={14} />
                      </Btn>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className={styles.sectionCard}>
            <div className="row">
              <h3>Documenten</h3>
              <span className={`ml-auto mono ${styles.docsMeta}`}>
                {documents.length} documenten
              </span>
            </div>
            {documents.length === 0 ? (
              <div className="mono c-ink-3">Nog geen documenten voor deze fase.</div>
            ) : (
              <div className={`col ${styles.docsList}`}>
                {documents.map((doc, i) => (
                  <div
                    key={doc.id}
                    className={`row gap-12 ${styles.docRow} ${
                      i === documents.length - 1 ? styles.docRowLast : ''
                    }`}
                  >
                    <div className={styles.docIcon}>
                      <Icon name={iconForMime(doc.mime)} size={15} color="var(--ink-2)" />
                    </div>
                    <div className={`flex-1 ${styles.docBody}`}>
                      <div className={styles.docTitle}>{doc.name}</div>
                      <div className={styles.docMetaRow}>v{toNumber(doc.version)}</div>
                    </div>
                    <Pill kind="idle" dot={false}>
                      {tagForMime(doc.mime)}
                    </Pill>
                    <span className={`mono ${styles.docWhen}`}>
                      {formatRelativePast(doc.uploadedAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className={styles.sectionCard}>
            <h3 className={styles.sectionTitle}>Beslissingen in deze fase</h3>
            {decisions.length === 0 ? (
              <div className="mono c-ink-3">Geen beslissingen geregistreerd voor deze fase.</div>
            ) : (
              <div className={`col gap-10 ${styles.decisionList}`}>
                {decisions.map((d) => {
                  const open = d.status === 'Open';
                  return (
                    <div
                      key={d.id}
                      className={`row gap-12 ${styles.decisionRow} ${
                        open ? styles.decisionRowDoing : styles.decisionRowDone
                      }`}
                    >
                      <Icon
                        name="decision"
                        size={14}
                        color={open ? 'var(--accent-2)' : 'var(--ink-3)'}
                      />
                      <div className="flex-1">
                        <div className={styles.decisionTitle}>{d.title}</div>
                        <div className={styles.decisionOption}>
                          {open ? `${d.options.length} opties` : (d.chosen ?? d.options[0] ?? '')}
                        </div>
                      </div>
                      <span
                        className={`mono ${
                          open ? styles.decisionStateDoing : styles.decisionStateDone
                        }`}
                      >
                        {open ? formatRelativeDeadline(d.deadline) : 'beslist'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        <div className={`col gap-16 ${styles.bodyRight}`}>
          <Card className={styles.sectionCard}>
            <div className="row">
              <h3>Foto-dagboek</h3>
              {photoTotal > 0 && (
                <Link to="/fotos" className={`ml-auto ${styles.photoLink}`}>
                  Bekijk alle {photoTotal} →
                </Link>
              )}
            </div>
            {photos.length === 0 ? (
              <div className="mono c-ink-3">Nog geen foto's voor deze fase.</div>
            ) : (
              <>
                <div className={styles.photoGrid}>
                  {photos.slice(0, PHOTO_GRID_SIZE).map((photo) => (
                    <div
                      key={photo.id}
                      className={`ph ${styles.photoTile}`}
                      title={photo.caption ?? ''}
                    >
                      {photo.caption ?? 'foto'}
                    </div>
                  ))}
                </div>
                <div className={`mono ${styles.photoMeta}`}>
                  {formatDayMonth(photos[0].takenAt)}
                  {photos[0].room ? ` · ${photos[0].room}` : ''}
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

function pascalCase(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1).toLowerCase();
}

function responsiblesForPhase(
  phaseId: ApiPhaseId,
  contacts: ContactDto[],
): Array<ContactDto & { primary: boolean }> {
  const wanted: Record<string, string[]> = {
    Ruw: ['Hoofdaannemer', 'Architect', 'Onderaannemer beton'],
    Wwd: ['Leverancier ramen'],
    Tech: ['Elektrieker'],
    Verg: ['Vergunningen'],
    Voor: ['EPB-verslaggever'],
  };
  const roles = wanted[phaseId] ?? [];
  return roles
    .map((role, idx) => {
      const c = contacts.find((cc) => cc.role === role);
      return c ? { ...c, primary: idx === 0 } : undefined;
    })
    .filter((c): c is ContactDto & { primary: boolean } => Boolean(c));
}

function iconForMime(mime: string): IconName {
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/zip') return 'archive';
  if (mime === 'application/pdf') return 'pdf';
  return 'paperclip';
}

function tagForMime(mime: string): string {
  if (mime.startsWith('image/')) return 'foto';
  if (mime === 'application/zip') return 'archief';
  return 'pdf';
}
